import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'

import {
  useAPIContentKinerjaUtama,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { assetUrl } from 'utils/url'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { IndikatorPerencanaanSelect } from 'app/KinerjaShared/components/IndikatorPerencanaanSelect'
import { useUpsertPerencanaan } from 'app/KinerjaGuruPage/hooks/use-upsert-perencanaan'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'

import type { OptionItem } from 'app/KinerjaShared/components/IndikatorPerencanaanSelect'
import type { UpsertSKPPayload } from 'types/kinerja'

type PengembanganKompetensiProps = {
  onNext: () => void
}

export function PengembanganKompetensi({
  onNext,
}: PengembanganKompetensiProps) {
  const router = useRouter()
  const { trackEvent } = useSKPTrackEvent()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour,
      },
      {
        tipe: 'SPF',
      }
    )
  // Safely get the first item of the `pengembanganKompetensi`
  // since it always have only 1 item
  const currentPengembanganKompetensi =
    perencanaanData?.data?.pengembanganKompetensi?.[0]

  // -----------------------------------------
  // Handle form data
  // -----------------------------------------
  const [selectedPengembanganKompetensi, setSelectedPengembanganKompetensi] =
    useState<OptionItem>(undefined)

  const handleChange = (selected: OptionItem) => {
    setSelectedPengembanganKompetensi({
      value: Number(selected.value),
      label: selected.label,
    })
  }

  const isDirty = useMemo(() => {
    return (
      selectedPengembanganKompetensi?.value !==
      currentPengembanganKompetensi?.id
    )
  }, [currentPengembanganKompetensi, selectedPengembanganKompetensi])

  // Sync with perencanaan data
  useEffect(() => {
    if (currentPengembanganKompetensi) {
      setSelectedPengembanganKompetensi({
        value: currentPengembanganKompetensi?.id,
        label: '',
      })
    }
  }, [currentPengembanganKompetensi])

  const isValidated = !!selectedPengembanganKompetensi?.value

  // -----------------------------------------
  // Get Pengembangan Kompetensi content
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'SPF',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )
  const pengembanganKompetensiContent =
    dataContentKinerjaUtama?.data?.pengembanganKompetensi
  const pengembanganKompetensiOptions: Array<OptionItem> = useMemo(
    () =>
      pengembanganKompetensiContent?.map((item) => ({
        value: item.id,
        label: item.rencanaHasilKerja || '',
      })) || [],
    [pengembanganKompetensiContent]
  )

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaan()
  const payload: UpsertSKPPayload = useMemo(
    () => ({
      id: perencanaanData?.data?.id,
      pengembanganKompetensi: selectedPengembanganKompetensi
        ? [{ id: Number(selectedPengembanganKompetensi.value), kuantitas: 1 }]
        : [{ id: undefined, kuantitas: 0 }],
      tipe: 'SPF',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perencanaanData, selectedPengembanganKompetensi?.value]
  )

  const handleSaveToDraft = () => {
    // Send track event
    trackEvent({
      event_name: 'CLICK_KINERJA_DRAFT_PLANNING',
      screen_name: 'KINERJA_PLANNING_PENGEMBANGAN_KOMPETENSI_PAGE',
      kinerja_planning_id: perencanaanData?.data?.id,
      kinerja_planning_status: perencanaanData?.data?.status,
    })

    // Do the upsert mutation
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      showSuccessMessage: true,
      onSuccess: () => router.push(`${PENGELOLAAN_KINERJA_GURU_PATH}`),
    })
  }

  const handleNext = () => {
    // Send track event
    trackEvent({
      event_name: 'CLICK_KINERJA_PLANNING_TO_PERILAKU_KERJA',
      screen_name: 'KINERJA_PLANNING_PENGEMBANGAN_KOMPETENSI_PAGE',
      kinerja_planning_id: perencanaanData?.data?.id,
      kinerja_planning_status: perencanaanData?.data?.status,
    })

    // Do the upsert mutation
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      onSuccess: onNext,
    })
  }

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingQueries = isPerencanaanLoading || isLoadingContentKinerjaUtama
  const isLoading = isLoadingQueries || isLoadingUpsertPerencanaan

  return (
    <>
      <div className="p-9">
        <div className="flex items-start gap-3 mb-6">
          <img
            src={assetUrl('/illustrations/img-document.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={64}
            height={64}
          />
          <div>
            <Text className="max-w-4xl mb-2" variant="heading-md">
              Pilih 1 Indikator untuk menjadi fokus peningkatan kinerja Anda
            </Text>
            <ul className="list-disc list-outside pl-5">
              <Text as="li">
                Diskusi bersama Kepala Sekolah terkait kegiatan yang dapat
                dilakukan untuk meningkatkan kompetensi berdasarkan indikator
                yang dipilih.
              </Text>
              <Text as="li">
                Di tahap Pelaksanaan, Anda akan melakukan kegiatan sesuai
                diskusi dan mengisi refleksi terhadap kegiatan yang sudah Anda
                lakukan.
              </Text>
            </ul>
          </div>
        </div>
        <IndikatorPerencanaanSelect
          isLoading={isLoading}
          allowReset={true}
          contents={{
            title: 'Indikator Kompetensi',
            orderNumber: 1,
          }}
          options={pengembanganKompetensiOptions}
          selected={selectedPengembanganKompetensi}
          onChange={handleChange}
        />
      </div>
      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          onClick={handleSaveToDraft}
          disabled={isPerencanaanLoading || !isDirty}
        >
          Simpan draf
        </Button>

        <Button
          color="black"
          size="lg"
          disabled={
            isPerencanaanLoading || isLoadingUpsertPerencanaan || !isValidated
          }
          onClick={handleNext}
        >
          Ke Perilaku Kerja
        </Button>
      </div>
    </>
  )
}
